import { useState } from "react"
import {Calendar, Clock, CheckCircle2, Phone, Video, Shield, Settings, MessageCircleDashed as MessageCircle, AlertTriangle} from 'lucide-react'
import DatePicker from "@/components/DatePicker"
import SectionHeading from "@/components/SectionHeading"
import { lumi } from "@/lib/lumi"
import toast from "react-hot-toast"

const types = [
  { value: "Appel découverte", label: "Appel découverte", icon: Phone, desc: "Premier échange pour comprendre vos besoins" },
  { value: "Rendez-vous technique", label: "Rendez-vous technique", icon: Settings, desc: "Discussion approfondie sur un sujet technique" },
  { value: "Audit IT", label: "Audit IT", icon: Shield, desc: "Évaluation complète de votre infrastructure" },
  { value: "Assistance planifiée", label: "Assistance planifiée", icon: Video, desc: "Intervention programmée à distance ou sur site" },
]

export default function AppointmentPage() {
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState({
    nom: "",
    email: "",
    telephone: "",
    entreprise: "",
    type: "",
    datePreferee: "",
    heurePreferee: "",
    details: "",
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      await lumi.functions.invoke("notify-admin", {
        method: "POST",
        body: {
          type: "appointment",
          data: {
            nom: form.nom,
            email: form.email,
            telephone: form.telephone,
            entreprise: form.entreprise,
            type: form.type,
            datePreferee: form.datePreferee,
            heurePreferee: form.heurePreferee,
            details: form.details,
            adminEmail: "contact@ljj-it.ch",
          },
        },
      })
      setSubmitted(true)
      toast.success("Demande de rendez-vous envoyée !")
      setForm({ nom: "", email: "", telephone: "", entreprise: "", type: "", datePreferee: "", heurePreferee: "", details: "" })
    } catch (error: any) {
      const errorMessage = error?.data?.error || "Une erreur est survenue lors de l'envoi."
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  // Date minimum : demain (pas de réservation le jour même)
  const tomorrow = (() => {
    const d = new Date()
    d.setDate(d.getDate() + 1)
    return d.toISOString().split("T")[0]
  })()

  return (
    <div className="min-h-screen pt-24 lg:pt-32">
      <section className="section-padding pb-16">
        <SectionHeading
          badge="Rendez-vous"
          title="Planifiez un"
          highlight="échange"
          description="Choisissez le type de rendez-vous qui correspond à votre besoin et sélectionnez une date qui vous convient."
        />
      </section>

      {/* Bandeau urgence WhatsApp */}
      <section className="section-padding pb-8">
        <a
          href="https://wa.me/41792489590"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-4 max-w-3xl mx-auto p-5 rounded-xl border border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10 transition-all duration-200 group"
        >
          <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center shrink-0">
            <MessageCircle className="w-6 h-6 text-emerald-400" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-0.5">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-bold text-white">Urgence ? Contactez-nous maintenant</span>
            </div>
            <p className="text-xs text-slate-400">
              Les rendez-vous ne peuvent pas être réservés le jour même. Pour un besoin urgent, contactez-nous directement via WhatsApp.
            </p>
          </div>
          <div className="shrink-0 px-4 py-2 rounded-lg bg-emerald-500 text-white text-sm font-semibold group-hover:bg-emerald-400 transition-colors">
            WhatsApp
          </div>
        </a>
      </section>

      <section className="section-padding pb-20">
        {submitted ? (
          <div className="max-w-lg mx-auto text-center card !p-12">
            <CheckCircle2 className="w-20 h-20 text-emerald-400 mx-auto mb-6" />
            <h3 className="text-2xl font-bold text-white mb-3">Demande envoyée !</h3>
            <p className="text-slate-400 mb-6">
              Votre demande de rendez-vous a bien été transmise. Nous vous contacterons rapidement pour confirmer le créneau.
            </p>
            <button onClick={() => setSubmitted(false)} className="btn-secondary">
              Planifier un autre rendez-vous
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="max-w-3xl mx-auto space-y-8">
            {/* Type de rendez-vous */}
            <div>
              <label className="label-field text-base mb-4 block">Type de rendez-vous *</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {types.map((t) => {
                  const Icon = t.icon
                  const selected = form.type === t.value
                  return (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => setForm({ ...form, type: t.value })}
                      className={`card !p-4 text-left transition-all duration-200 ${
                        selected
                          ? "!border-cyan-500 !bg-cyan-500/10 ring-1 ring-cyan-500/30"
                          : "hover:!border-slate-500"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                          selected ? "bg-cyan-500/20" : "bg-slate-700/50"
                        }`}>
                          <Icon className={`w-5 h-5 ${selected ? "text-cyan-400" : "text-slate-400"}`} />
                        </div>
                        <div>
                          <div className={`text-sm font-semibold ${selected ? "text-cyan-300" : "text-white"}`}>
                            {t.label}
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5">{t.desc}</div>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
              {!form.type && (
                <input type="text" required value="" className="sr-only" onChange={() => {}} tabIndex={-1} />
              )}
            </div>

            {/* Informations personnelles */}
            <div className="card !p-6">
              <h4 className="text-lg font-semibold text-white mb-4">Vos informations</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="label-field">Nom complet *</label>
                  <input
                    type="text"
                    required
                    className="input-field"
                    placeholder="Jean Dupont"
                    value={form.nom}
                    onChange={(e) => setForm({ ...form, nom: e.target.value })}
                  />
                </div>
                <div>
                  <label className="label-field">Email *</label>
                  <input
                    type="email"
                    required
                    className="input-field"
                    placeholder="jean@entreprise.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                <div>
                  <label className="label-field">Téléphone *</label>
                  <input
                    type="tel"
                    required
                    className="input-field"
                    placeholder="+41 79 123 45 67"
                    value={form.telephone}
                    onChange={(e) => setForm({ ...form, telephone: e.target.value })}
                  />
                </div>
                <div>
                  <label className="label-field">Entreprise</label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Nom de votre société"
                    value={form.entreprise}
                    onChange={(e) => setForm({ ...form, entreprise: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Date et heure */}
            <div className="card !p-6">
              <h4 className="text-lg font-semibold text-white mb-5 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-cyan-400" />
                Date et heure souhaitées
              </h4>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <DatePicker
                  label="Date souhaitée"
                  required
                  value={form.datePreferee}
                  onChange={(date) => setForm({ ...form, datePreferee: date })}
                  minDate={tomorrow}
                />
                <div className="flex flex-col">
                  <label className="label-field flex items-center gap-1.5 mb-2">
                    <Clock className="w-4 h-4 text-cyan-400" />
                    Heure souhaitée *
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {["08:00", "09:00", "10:00", "11:00", "13:00", "14:00", "15:00", "16:00", "17:00"].map((h) => {
                      const selected = form.heurePreferee === h
                      return (
                        <button
                          key={h}
                          type="button"
                          onClick={() => setForm({ ...form, heurePreferee: h })}
                          className={`py-2.5 px-3 rounded-lg text-sm font-medium transition-all duration-150 border ${
                            selected
                              ? "bg-cyan-500 text-white border-cyan-500 shadow-lg shadow-cyan-500/30"
                              : "bg-slate-800/60 text-slate-400 border-slate-700/60 hover:border-cyan-500/40 hover:text-cyan-300 hover:bg-cyan-500/5"
                          }`}
                        >
                          {h.replace(":", "h")}
                        </button>
                      )
                    })}
                  </div>
                  <p className="text-xs text-slate-600 mt-2">Créneaux disponibles : lundi au vendredi, 08h00 - 18h00</p>
                  {!form.heurePreferee && (
                    <input type="text" required value="" className="sr-only" onChange={() => {}} tabIndex={-1} />
                  )}
                </div>
              </div>
            </div>

            {/* Détails */}
            <div className="card !p-6">
              <h4 className="text-lg font-semibold text-white mb-4">Détails supplémentaires</h4>
              <textarea
                rows={4}
                className="input-field resize-none"
                placeholder="Décrivez brièvement votre besoin ou le contexte de votre demande..."
                value={form.details}
                onChange={(e) => setForm({ ...form, details: e.target.value })}
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || !form.type}
              className="btn-primary w-full justify-center !py-4 text-base disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Calendar className="w-5 h-5" />
                  Envoyer ma demande de rendez-vous
                </>
              )}
            </button>

            <p className="text-center text-xs text-slate-500">
              Nous vous recontacterons sous 24h pour confirmer le créneau. L'email de confirmation sera envoyé à l'adresse indiquée.
            </p>
          </form>
        )}
      </section>
    </div>
  )
}
